// Phase S4 (đợt 261030) — logic thuần cho «Làm tươi dàn dựng» (status.stagingRefresh · nextAction.kind 'REFRESH_STAGING' · 409 STAGING_STALE).
// Hợp đồng DTO: `_workers/reports/s-fe-contract.md` mục «S4b». KHÔNG import React / antd / router để jest chạy được.
// Prompt «lệch dữ liệu» (promptEstimate.staleN/stale[], PROMPT_STALE) nằm ở `workflowFourVideoUtils.ts`.
import { stripMoneyOf } from './moneyText';

/** `stagingRefresh.stale` — số đo BE; `usd` là MỨC TRÊN (scenes × unitUsd). FE không tự tính tiền. */
export interface StagingStale { scenes: number; events: number; sceneIds?: string[]; eventIds?: string[]; unitUsd: number; usd: number; hash: string }
export interface StagingRefreshLastRun { runId?: string; state?: string; endedAt?: string | null; error?: { code?: string; message?: string } | null }
export interface StagingRefresh {
  stale: StagingStale;
  canRun: boolean;
  why: { code?: string; message?: string } | string | null;
  action?: { kind?: string; label?: string } | null;
  lastRun?: StagingRefreshLastRun | null;
  /** S10 (G4): BE giữ nút chính «Gen N video» (không giật sang «Làm tươi») — dàn dựng cũ chỉ là cảnh báo phụ có nút riêng. */
  genStillAllowed?: boolean;
}

type StatusLike = {
  stagingRefresh?: StagingRefresh | null;
  nextAction?: { kind?: string; step?: string; enabled?: boolean } | null;
  gates?: { STEP1?: { verdict?: string } | null } | null;
} | null | undefined;

const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

/** Hình dạng `stale` hợp lệ (có hash chuỗi và ≥ 1 cảnh) ⇒ dùng; không ⇒ null. */
export function stagingStaleOf(raw: unknown): StagingStale | null {
  const s = raw as Partial<StagingStale> | null | undefined;
  if (!s || typeof s !== 'object' || typeof s.hash !== 'string' || !s.hash || num(s.scenes) <= 0) return null;
  return { scenes: num(s.scenes), events: num(s.events), sceneIds: Array.isArray(s.sceneIds) ? s.sceneIds.map(String) : undefined, eventIds: Array.isArray(s.eventIds) ? s.eventIds.map(String) : undefined, unitUsd: num(s.unitUsd), usd: num(s.usd), hash: s.hash };
}
/** `status.stagingRefresh` hợp lệ và chạy được (`canRun`); không có cảnh cũ / chuỗi đang chạy ⇒ BE trả null ⇒ null. */
export function stagingRefreshOf(status: StatusLike): StagingRefresh | null {
  const r = status?.stagingRefresh;
  const stale = stagingStaleOf(r?.stale);
  if (!r || !stale) return null;
  return { ...r, stale };
}
/** Nhãn nút: ưu tiên nhãn BE (`action.label`), không có ⇒ dựng từ số đo của BE. */
export function stagingRefreshLabelOf(r: Pick<StagingRefresh, 'stale' | 'action'>): string {
  const fromBe = r.action?.label?.trim();
  // Nhãn BE có thể kèm «≈$x»: giao diện không hiện tiền ⇒ lọc (số cảnh giữ nguyên).
  return (fromBe ? stripMoneyOf(fromBe) : '') || `Làm tươi dàn dựng (${r.stale.scenes} cảnh)`;
}
/** `nextAction.kind === 'REFRESH_STAGING'` — nút chính của bước ① là «Làm tươi dàn dựng», không phải «Duyệt cổng 1». */
export const stagingNextActionOf = (status: StatusLike): boolean => status?.nextAction?.kind === 'REFRESH_STAGING' && status.nextAction.enabled !== false;
/** Còn cảnh cũ mà cổng 1 chưa OK ⇒ không cho bấm «Duyệt cổng 1» (BE sẽ 409 STAGING_STALE). */
export const stagingBlocksGate1Of = (status: StatusLike): boolean => !!stagingRefreshOf(status) && status?.gates?.STEP1?.verdict !== 'OK';

export interface StagingBanner { title: string; description: string; lastRunError: string | null; label: string; canRun: boolean; why: string | null }
export function stagingBannerOf(r: StagingRefresh, o: { gate1Blocked?: boolean } = {}): StagingBanner {
  const { scenes, events } = r.stale;
  const whyText = typeof r.why === 'string' ? r.why : r.why?.message ?? null;
  const lastErr = r.lastRun?.error?.message?.trim() || (r.lastRun?.state && /FAIL|ERROR/i.test(r.lastRun.state) ? 'Lần làm tươi gần nhất bị lỗi.' : null);
  return {
    title: `Dàn dựng của ${scenes} cảnh (${events} video) đã cũ`,
    description: r.genStillAllowed
      ? 'Kịch bản / đạo cụ / hiện diện đã đổi sau lần máy dàn dựng gần nhất. «Gen video» vẫn bấm được; làm tươi (không bắt buộc) để prompt và ảnh khớp dữ liệu mới — không chạy lại bước ①.'
      : `Dữ liệu dàn dựng cần làm tươi ${o.gate1Blocked ? 'trước khi duyệt cổng 1' : 'trước khi viết prompt'}. Không chạy lại bước ①.`,
    lastRunError: lastErr, label: stagingRefreshLabelOf(r), canRun: r.canRun !== false, why: whyText,
  };
}

/** Hộp xác nhận (tiền thật nằm ở BE: estimateHash; FE chỉ nêu số cảnh / video, không hiện tiền). */
export function stagingRefreshBoxOf(stale: StagingStale): { title: string; lead: string; note: string; okText: string } {
  return {
    title: `Làm tươi dàn dựng ${stale.scenes} cảnh?`,
    lead: `Làm tươi dàn dựng ${stale.scenes} cảnh (${stale.events} video). Không chạy lại bước ①.`,
    note: `Máy (AI chữ) dàn lại đúng các cảnh đang cũ, phần đã xong của bước ① được giữ nguyên.`,
    okText: 'Làm tươi dàn dựng',
  };
}

export interface StagingRefreshBody { requestId: string; confirm: true; estimateHash: string }
export const newStagingRefreshBody = (a: { stale: StagingStale; requestId: string }): StagingRefreshBody => ({ requestId: a.requestId, confirm: true, estimateHash: a.stale.hash });

/** requestId giữ nguyên khi thử lại CÙNG ước (mất mạng / 504); ước đổi (hash khác) ⇒ id mới. */
export type StagingRequestMemo = { hash: string; requestId: string } | null;
export function stagingRequestIdOf(memo: StagingRequestMemo, hash: string, make: () => string): { requestId: string; memo: StagingRequestMemo } {
  if (memo && memo.hash === hash) return { requestId: memo.requestId, memo };
  const next = { hash, requestId: make() };
  return { requestId: next.requestId, memo: next };
}

/** `POST /plans/:id/staging/refresh` thành công: 202 `{started:true}` · 200 `{started:false, reason:'NOTHING_TO_DO'}`. */
export function stagingRefreshOutcomeOf(response: any): { kind: 'started' | 'nothing' | 'unknown'; level: 'success' | 'info' | 'warning'; message: string; done: boolean } {
  if (response?.started === true || response?.accepted === true) {
    const n = num(response?.scenes);
    return { kind: 'started', level: 'success', done: true, message: `Máy đang làm tươi dàn dựng${n ? ` ${n} cảnh` : ''} — xong thì nút này tự biến mất. Bạn có thể rời trang.` };
  }
  if (response?.reason === 'NOTHING_TO_DO') return { kind: 'nothing', level: 'info', done: true, message: 'Dàn dựng đã tươi — không có gì cần làm. Đã tải lại.' };
  return { kind: 'unknown', level: 'warning', done: false, message: 'Máy chưa mở việc làm tươi. Hãy tải lại để xem trạng thái.' };
}

const dataOf = (err: any): any => err?.response?.data;
const codeOf = (err: any): string | null => { const d = dataOf(err); const c = d?.code ?? d?.errorCode; return typeof c === 'string' ? c : null; };
/** BE trả `{ code, message, ...extra }` (phẳng) — chấp nhận cả `extra` lồng. `stagingRefresh` ở lỗi là chính `stale` (không có lớp `.stale`); chấp nhận cả hai. */
export function stagingStaleFromErrorOf(err: any): StagingStale | null {
  const d = dataOf(err);
  const raw = d?.stagingRefresh ?? d?.extra?.stagingRefresh;
  return stagingStaleOf(raw?.stale ?? raw);
}
export type StagingErrorCode = 'STAGING_STALE' | 'STAGING_ESTIMATE_CHANGED';
export interface StagingErrorView { code: StagingErrorCode; text: string; stale: StagingStale | null }
/** 409 `STAGING_STALE` (duyệt cổng 1 · prompt/run · duyệt cổng 3 kèm startPrompt · Chạy tiếp đoạn 10) và 409 `STAGING_ESTIMATE_CHANGED` (ước làm tươi đổi). Mã khác ⇒ null. */
export function stagingErrorViewOf(err: any): StagingErrorView | null {
  const code = codeOf(err);
  if (code !== 'STAGING_STALE' && code !== 'STAGING_ESTIMATE_CHANGED') return null;
  const stale = stagingStaleFromErrorOf(err);
  const server = typeof dataOf(err)?.message === 'string' ? (stripMoneyOf(dataOf(err).message.trim()) ?? '') : '';
  const text = code === 'STAGING_ESTIMATE_CHANGED'
    ? 'Số cảnh hoặc chi phí làm tươi dàn dựng vừa đổi so với lúc bạn xem — xem số mới rồi xác nhận lại.'
    : server || 'Dàn dựng của một số cảnh đã cũ — làm tươi dàn dựng trước.';
  return { code, text, stale };
}
/** Lỗi riêng của route làm tươi (ngoài STAGING_*): BAD_BODY. Mã khác ⇒ null (rơi về `apiErrorText` / việc chặn). */
export function stagingRefreshErrorTextOf(err: any): string | null {
  const view = stagingErrorViewOf(err);
  if (view) return view.text;
  if (codeOf(err) === 'BAD_BODY') return 'Yêu cầu không hợp lệ — tải lại trang rồi thử lại.';
  return null;
}
/** Phản hồi duyệt cổng 3 kèm `startPrompt`: `prompt.error.code === 'STAGING_STALE'` (cổng vẫn được ghi). */
export const stagingStaleInPromptOf = (response: any): boolean => response?.prompt?.error?.code === 'STAGING_STALE';
